import { useEffect } from "react";
import { useQuery } from "convex/react";
import { Line, LineChart, ResponsiveContainer, YAxis } from "recharts";
import { api } from "../../../convex/_generated/api";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";
import { ConfidenceChip } from "./ConfidenceChip";
import { expenditureBadge, expenditureLabel } from "./utils";

export function ExpenditureCard() {
  const engineState = useQuery(api.engine.getEngineState, {});
  const history = useQuery(api.engine.getExpenditureHistory, { days: 90 });

  useEffect(() => {
    if (history !== undefined) {
      console.debug("[Frontend][ExpenditureCard] Expenditure history loaded", { points: history.length });
    }
  }, [history]);

  if (engineState === undefined) {
    return <Skeleton className="h-36 rounded-2xl" data-testid="expenditure-loading" />;
  }
  if (engineState === null) return null;

  const sparkPoints = (history ?? []).map((point, index) => ({ index, kcal: point.kcal }));
  const showSparkline = sparkPoints.length >= 2;

  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="expenditure-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-text">{expenditureLabel(engineState.expenditureKind)}</h3>
          <p className="mt-1 text-2xl font-bold text-accent" data-testid="expenditure-value">
            {engineState.expenditureKcal !== null ? `${formatNumber(engineState.expenditureKcal)} kcal` : "—"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span
            className={cn(
              "inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold",
              engineState.expenditureKind === "personalised" ? "bg-accent text-white" : "bg-muted text-text-muted",
            )}
            data-testid="expenditure-badge"
          >
            {expenditureBadge(engineState.expenditureKind)}
          </span>
          <ConfidenceChip level={engineState.expenditureConfidence} testId="expenditure-confidence" />
        </div>
      </div>
      {showSparkline && (
        <div className="h-[60px] w-full" data-testid="expenditure-sparkline">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkPoints} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <YAxis hide domain={["dataMin - 20", "dataMax + 20"]} />
              <Line type="monotone" dataKey="kcal" stroke="#2f855a" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-xs leading-relaxed text-text-muted">
        Updated from your logged food and weight trend — not from wearable burn.
      </p>
    </Card>
  );
}
